import { createFileRoute } from '@tanstack/react-router'
import { useMemo } from 'react'

import { useGetAvailableExports, useListSalesChannels, useListProductGroups } from '@/api/react-query'
import { useApiContext } from '@/api/react-query'
import { Button } from '@/components/base/button'
import { Container, ContainerHeader } from '@/components/base/container'
import { Heading } from '@/components/base/heading'
import { LoadingSpinner } from '@/components/base/loading-spinner'
import { toast } from '@/components/base/toast'
import { FormContainer, FormFieldGroup, useAppForm } from '@/components/form/form'
import { SingleColumnPage } from '@/components/page-layout'
import { useI18n } from '@/i18n/i18n-react'

export const Route = createFileRoute('/_authenticated/settings/exports/')({
	component: ExportsPage
})

function ExportsPage() {
	const { L } = useI18n()
	const { data: availableExports, isPending } = useGetAvailableExports({})

	if (isPending) {
		return <LoadingSpinner centered />
	}

	return (
		<SingleColumnPage>
			<div className='space-y-6'>
				<div>
					<Heading level='h1'>{L.Settings.Exports?.Title?.() || 'Exports'}</Heading>
					<p className='text-muted-foreground mt-2 text-sm'>
						{L.Settings.Exports?.Description?.() || 'Export data to various formats and systems'}
					</p>
				</div>

				{availableExports?.pricer && <PricerExportSection />}
				{availableExports?.visma && <VismaExportSection />}
				{availableExports?.digiScale && <DigiScaleExportSection />}
				{availableExports?.saft && <SAFTExportSection />}
			</div>
		</SingleColumnPage>
	)
}

function PricerExportSection() {
	const { L } = useI18n()
	const { api } = useApiContext()
	const salesChannels = useListSalesChannels()

	const salesChannelOptions = useMemo(
		() =>
			salesChannels.data?.map(channel => ({
				label: channel.name,
				value: channel.uuid
			})) || [],
		[salesChannels.data]
	)

	const form = useAppForm({
		defaultValues: {
			salesChannelUuid: ''
		},
		onSubmit: async ({ value }) => {
			try {
				const response = await api.pricerProductExport({
					body: { salesChannelUuid: value.salesChannelUuid }
				})

				// Download CSV
				const blob = new Blob([response as any], { type: 'text/csv' })
				const url = URL.createObjectURL(blob)
				const a = document.createElement('a')
				a.href = url
				a.download = `pricer-products-${Date.now()}.csv`
				document.body.appendChild(a)
				a.click()
				document.body.removeChild(a)
				URL.revokeObjectURL(url)

				toast.success(L.Settings.Exports?.PricerSuccess?.() || 'Pricer export successful')
			} catch (error: any) {
				toast.error(error.message || 'Failed to export Pricer data')
			}
		}
	})

	return (
		<Container>
			<ContainerHeader heading={L.Settings.Exports?.PricerTitle?.() || 'Pricer Product Export'} />
			<div className='p-6'>
				<FormContainer>
					<div className='space-y-4'>
						<FormFieldGroup>
							<form.AppField
								name='salesChannelUuid'
								children={field => (
									<field.ComboboxField
										label={L.Fields.SalesChannel()}
										placeholder='Select sales channel'
										options={salesChannelOptions}
										optionsLoading={salesChannels.isLoading}
									/>
								)}
							/>
						</FormFieldGroup>

						<div className='flex justify-end'>
							<Button
								type='submit'
								disabled={form.state.isSubmitting}
								isLoading={form.state.isSubmitting}
							>
								{L.Settings.Exports?.Export?.() || 'Export'}
							</Button>
						</div>
					</div>
				</FormContainer>
			</div>
		</Container>
	)
}

function VismaExportSection() {
	const { L } = useI18n()
	const { api } = useApiContext()

	const form = useAppForm({
		defaultValues: {
			startDate: '',
			endDate: ''
		},
		onSubmit: async ({ value }) => {
			try {
				const startTimestamp = value.startDate ? new Date(value.startDate).getTime() : Date.now()
				const endTimestamp = value.endDate ? new Date(value.endDate).getTime() : undefined

				await api.vismaExport({
					body: {
						startDate: startTimestamp,
						endDate: endTimestamp
					}
				})

				toast.success(L.Settings.Exports?.VismaSuccess?.() || 'Visma export successful')
			} catch (error: any) {
				toast.error(error.message || 'Failed to export Visma data')
			}
		}
	})

	return (
		<Container>
			<ContainerHeader heading={L.Settings.Exports?.VismaTitle?.() || 'Visma Export'} />
			<div className='p-6'>
				<FormContainer>
					<div className='space-y-4'>
						<FormFieldGroup>
							<form.AppField
								name='startDate'
								children={field => (
									<field.TextField label={L.Fields.StartDate?.() || 'Start Date'} type='date' />
								)}
							/>
						</FormFieldGroup>

						<FormFieldGroup>
							<form.AppField
								name='endDate'
								children={field => (
									<field.TextField
										label={L.Fields.EndDate?.() || 'End Date'}
										type='date'
										placeholder='Optional'
									/>
								)}
							/>
						</FormFieldGroup>

						<div className='flex justify-end'>
							<Button
								type='submit'
								disabled={form.state.isSubmitting}
								isLoading={form.state.isSubmitting}
							>
								{L.Settings.Exports?.Export?.() || 'Export'}
							</Button>
						</div>
					</div>
				</FormContainer>
			</div>
		</Container>
	)
}

function DigiScaleExportSection() {
	const { L } = useI18n()
	const { api } = useApiContext()
	const salesChannels = useListSalesChannels()
	const productGroups = useListProductGroups({})

	const salesChannelOptions = useMemo(
		() =>
			salesChannels.data?.map(channel => ({
				label: channel.name,
				value: channel.uuid
			})) || [],
		[salesChannels.data]
	)

	const productGroupOptions = useMemo(
		() =>
			productGroups.data?.groups?.map(group => ({
				label: group.name,
				value: group.uuid
			})) || [],
		[productGroups.data]
	)

	const form = useAppForm({
		defaultValues: {
			salesChannelUuids: [] as string[],
			groupUuids: [] as string[]
		},
		onSubmit: async ({ value }) => {
			try {
				const response = await api.digiScaleProductExport({
					body: {
						salesChannelUuids: value.salesChannelUuids,
						groupUuids: value.groupUuids
					}
				})

				// Download XML
				const blob = new Blob([response.data as any], { type: 'text/xml' })
				const url = URL.createObjectURL(blob)
				const a = document.createElement('a')
				a.href = url
				a.download = `digiscale-products-${Date.now()}.xml`
				document.body.appendChild(a)
				a.click()
				document.body.removeChild(a)
				URL.revokeObjectURL(url)

				toast.success(L.Settings.Exports?.DigiScaleSuccess?.() || 'DigiScale export successful')
			} catch (error: any) {
				toast.error(error.message || 'Failed to export DigiScale data')
			}
		}
	})

	return (
		<Container>
			<ContainerHeader heading={L.Settings.Exports?.DigiScaleTitle?.() || 'DigiScale Product Export'} />
			<div className='p-6'>
				<FormContainer>
					<div className='space-y-4'>
						<FormFieldGroup>
							<form.AppField
								name='salesChannelUuids'
								children={field => (
									<field.ComboboxField
										label={L.Fields.SalesChannel()}
										placeholder='Select sales channels'
										multiple
										options={salesChannelOptions}
										optionsLoading={salesChannels.isLoading}
									/>
								)}
							/>
						</FormFieldGroup>

						<FormFieldGroup>
							<form.AppField
								name='groupUuids'
								children={field => (
									<field.ComboboxField
										label={L.Fields.Groups()}
										placeholder='Select product groups'
										multiple
										options={productGroupOptions}
										optionsLoading={productGroups.isLoading}
									/>
								)}
							/>
						</FormFieldGroup>

						<div className='flex justify-end'>
							<Button
								type='submit'
								disabled={form.state.isSubmitting}
								isLoading={form.state.isSubmitting}
							>
								{L.Settings.Exports.Export()}
							</Button>
						</div>
					</div>
				</FormContainer>
			</div>
		</Container>
	)
}

function SAFTExportSection() {
	const { L } = useI18n()
	const { api } = useApiContext()

	const cashRegisterForm = useAppForm({
		defaultValues: {
			year: new Date().getFullYear(),
			month: new Date().getMonth() + 1
		},
		onSubmit: async ({ value }) => {
			try {
				const response = await api.sAFTExportCashRegister({
					body: { year: value.year, month: value.month }
				})

				// Download XML
				const blob = new Blob([response as any], { type: 'text/xml' })
				const url = URL.createObjectURL(blob)
				const a = document.createElement('a')
				a.href = url
				a.download = `saft-cash-register-${value.year}-${value.month}.xml`
				document.body.appendChild(a)
				a.click()
				document.body.removeChild(a)
				URL.revokeObjectURL(url)

				toast.success(L.Settings.Exports?.SAFTSuccess?.() || 'SAFT export successful')
			} catch (error: any) {
				toast.error(error.message || 'Failed to export SAFT data')
			}
		}
	})

	const financialForm = useAppForm({
		defaultValues: {
			startDate: '',
			endDate: ''
		},
		onSubmit: async ({ value }) => {
			try {
				const startTimestamp = value.startDate ? new Date(value.startDate).getTime() : Date.now()
				const endTimestamp = value.endDate ? new Date(value.endDate).getTime() : undefined

				const response = await api.sAFTExportFinancial({
					body: {
						startDate: startTimestamp,
						endDate: endTimestamp
					}
				})

				// Download XML
				const blob = new Blob([response as any], { type: 'text/xml' })
				const url = URL.createObjectURL(blob)
				const a = document.createElement('a')
				a.href = url
				a.download = `saft-financial-${Date.now()}.xml`
				document.body.appendChild(a)
				a.click()
				document.body.removeChild(a)
				URL.revokeObjectURL(url)

				toast.success(L.Settings.Exports?.SAFTSuccess?.() || 'SAFT export successful')
			} catch (error: any) {
				toast.error(error.message || 'Failed to export SAFT data')
			}
		}
	})

	return (
		<Container>
			<ContainerHeader heading={L.Settings.Exports?.SAFTTitle?.() || 'SAFT Export'} />
			<div className='space-y-6 p-6'>
				<div>
					<Heading level='h3' className='mb-4'>
						{L.Settings.Exports?.SAFTCashRegister?.() || 'Cash Register'}
					</Heading>
					<FormContainer>
						<div className='space-y-4'>
							<div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
								<FormFieldGroup>
									<cashRegisterForm.AppField
										name='year'
										children={field => (
											<field.TextField label={L.Fields.Year?.() || 'Year'} type='number' />
										)}
									/>
								</FormFieldGroup>

								<FormFieldGroup>
									<cashRegisterForm.AppField
										name='month'
										children={field => (
											<field.TextField
												label={L.Fields.Month?.() || 'Month'}
												type='number'
												min={1}
												max={12}
											/>
										)}
									/>
								</FormFieldGroup>
							</div>

							<div className='flex justify-end'>
								<Button
									type='submit'
									disabled={cashRegisterForm.state.isSubmitting}
									isLoading={cashRegisterForm.state.isSubmitting}
								>
									{L.Settings.Exports?.Export?.() || 'Export'}
								</Button>
							</div>
						</div>
					</FormContainer>
				</div>

				<div>
					<Heading level='h3' className='mb-4'>
						{L.Settings.Exports?.SAFTFinancial?.() || 'Financial'}
					</Heading>
					<FormContainer>
						<div className='space-y-4'>
							<FormFieldGroup>
								<financialForm.AppField
									name='startDate'
									children={field => (
										<field.TextField label={L.Fields.StartDate?.() || 'Start Date'} type='date' />
									)}
								/>
							</FormFieldGroup>

							<FormFieldGroup>
								<financialForm.AppField
									name='endDate'
									children={field => (
										<field.TextField
											label={L.Fields.EndDate?.() || 'End Date'}
											type='date'
											placeholder='Optional'
										/>
									)}
								/>
							</FormFieldGroup>

							<div className='flex justify-end'>
								<Button
									type='submit'
									disabled={financialForm.state.isSubmitting}
									isLoading={financialForm.state.isSubmitting}
								>
									{L.Settings.Exports?.Export?.() || 'Export'}
								</Button>
							</div>
						</div>
					</FormContainer>
				</div>
			</div>
		</Container>
	)
}
